import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ArrowLeft, ArrowRight } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getBlogPostBySlug, blogPosts } from "../../data/blogPosts";

export default function BlogPost() {
  const { slug, lang } = useParams();
  const { t } = useTranslation("blog");
  const navigate = useNavigate();
  const post = getBlogPostBySlug(slug);
  const prefix = lang ? `/${lang}` : "";
  const currentLang = lang || "en";

  if (!post) {
    navigate(`${prefix}/blog`, { replace: true });
    return null;
  }

  const sections = t(`posts.${post.slug}.sections`, { returnObjects: true });
  const faq = t(`posts.${post.slug}.faq`, { returnObjects: true });

  // Find prev/next posts
  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `https://trackteam.app/${currentLang}` },
      { "@type": "ListItem", "position": 2, "name": t("index.title"), "item": `https://trackteam.app/${currentLang}/blog` },
      { "@type": "ListItem", "position": 3, "name": t(`posts.${post.slug}.title`) },
    ],
  };

  const postImage = post.image ? `https://trackteam.app${post.image}` : "https://trackteam.app/TrackTeam/trackteamlogo.png";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": t(`posts.${post.slug}.title`),
    "description": t(`posts.${post.slug}.excerpt`),
    "image": postImage,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": "TrackTeam",
      "url": "https://trackteam.app",
    },
    "publisher": {
      "@type": "Organization",
      "name": "TrackTeam",
      "logo": { "@type": "ImageObject", "url": "https://trackteam.app/TrackTeam/trackteamlogo.png" },
    },
  };

  const faqSchema = Array.isArray(faq) && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`posts.${post.slug}.title`)} — TrackTeam Blog`}
        description={t(`posts.${post.slug}.excerpt`)}
        schema={breadcrumbSchema}
        ogImage={postImage}
      />
      {/* Additional schemas */}
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}

      <Header />

      {/* Article */}
      <article className="pt-24 md:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb */}
          <Link to={`${prefix}/blog`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToBlog")}
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-medium text-[#62329f] bg-purple-50 px-2.5 py-1 rounded-full">
                {t(`categories.${post.category === "howto" ? "howTo" : post.category}`)}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock className="w-3 h-3" /> {post.readTime} {t("minRead")}
              </span>
              <span className="text-xs text-gray-400">{post.date}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
              {t(`posts.${post.slug}.title`)}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t(`posts.${post.slug}.excerpt`)}
            </p>
          </div>

          {/* Content Sections */}
          <div className="prose prose-lg max-w-none">
            {Array.isArray(sections) &&
              sections.map((section, i) => (
                <div key={i} className="mb-8">
                  {section.heading && (
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">{section.heading}</h2>
                  )}
                  {section.text && (
                    <p className="text-gray-700 leading-relaxed mb-4">{section.text}</p>
                  )}
                  {Array.isArray(section.bullets) && (
                    <ul className="space-y-2 mb-4">
                      {section.bullets.map((bullet, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <span className="text-[#62329f] mt-1.5">•</span>
                          <span className="text-gray-700">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>

          {/* FAQ */}
          {Array.isArray(faq) && faq.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t("faqTitle")}</h2>
              <div className="space-y-4">
                {faq.map((item, i) => (
                  <details key={i} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <summary className="px-6 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-100">
                      {item.q}
                    </summary>
                    <div className="px-6 pb-4 text-gray-600">{item.a}</div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Inline CTA */}
          <div className="mt-12 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 text-center border border-purple-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t("inlineCta.title")}</h3>
            <p className="text-gray-600 mb-6">{t("inlineCta.subtitle")}</p>
            <div className="flex justify-center gap-4">
              <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
                <img src="/Image/logo_appstore.svg" alt="App Store" className="h-11 w-[150px] object-contain" loading="lazy" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
                <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-11 w-[150px] object-contain" loading="lazy" />
              </a>
            </div>
          </div>

          {/* Prev/Next Navigation */}
          <div className="mt-12 pt-8 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            {prevPost ? (
              <Link to={`${prefix}/blog/${prevPost.slug}`} className="flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-400 mb-1">{t("previous")}</div>
                  <div className="text-sm font-medium text-gray-700 line-clamp-1">{t(`posts.${prevPost.slug}.title`)}</div>
                </div>
              </Link>
            ) : <div />}
            {nextPost && (
              <Link to={`${prefix}/blog/${nextPost.slug}`} className="flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors text-right md:justify-end">
                <div>
                  <div className="text-xs text-gray-400 mb-1">{t("next")}</div>
                  <div className="text-sm font-medium text-gray-700 line-clamp-1">{t(`posts.${nextPost.slug}.title`)}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              </Link>
            )}
          </div>
        </div>
      </article>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
